import type { ReactNode } from "react";

import { EmptyState, formatProviderFullName } from "@stacklet/ui";
import { graphql, usePreloadedQuery } from "react-relay";
import { generatePath, Navigate } from "react-router";

import { CircularFallback } from "app/components/fallbacks";
import { paths } from "app/constants/paths";
import { useProvider } from "app/contexts/ProviderContext";
import { useEnabledDashboardsFilterElement } from "app/hooks/feature-flags/useEnabledDashboards/useEnabledDashboardsFilterElement";
import { useRoutePreloadedQuery } from "app/hooks/relay/useRoutePreloadedQuery";

import { isCostDashboardKey } from "../utils/dashboardKeys";

import type {
  DashboardProviderGuardQuery,
  DashboardProviderGuardQuery$variables,
  FilterElementInput,
} from "./__generated__/DashboardProviderGuardQuery.graphql";
import type { PreloadedRoute } from "app/utils/routePreload";
import type { PreloadedQuery } from "react-relay";
import type { FetchPolicy } from "relay-runtime";

/**
 * How the guard's query is fetched, by the route loaders and the guard alike.
 * The sidebar's AppQuery already holds these dashboards, so the store usually
 * answers it without a request.
 */
export const DASHBOARD_PROVIDER_GUARD_FETCH_POLICY: FetchPolicy =
  "store-or-network";

/**
 * Builds the guard query's variables. Both the route loaders (see
 * dashboardPreload) and the guard call this, so that the guard recognises a
 * preloaded query as its own.
 */
export function dashboardProviderGuardVariables(
  filterElement: FilterElementInput,
): DashboardProviderGuardQuery$variables {
  return { filterElement };
}

const dashboardProviderGuardQuery = graphql`
  query DashboardProviderGuardQuery($filterElement: FilterElementInput) {
    dashboards(filterElement: $filterElement) {
      edges {
        node {
          key
        }
      }
    }
  }
`;

interface Props {
  /** The dashboard key from the current route (`:key`). */
  dashboardKey: string;
  /** Rendered when the route already matches the selected provider. */
  children: ReactNode;
  /**
   * The query the route loader started fetching. Absent when the guard is
   * rendered without a loader, and null when the loader didn't preload, so
   * the guard fetches on mount instead.
   */
  preloaded?: PreloadedRoute<DashboardProviderGuardQuery> | null;
}

/**
 * Redirects to the selected provider's cost dashboard when the route `:key`
 * belongs to a different provider, so the page matches the sidebar selection.
 */
export function DashboardProviderGuard({
  dashboardKey,
  children,
  preloaded,
}: Props) {
  const filterElement = useEnabledDashboardsFilterElement();

  const queryRef = useRoutePreloadedQuery<DashboardProviderGuardQuery>(
    dashboardProviderGuardQuery,
    preloaded,
    dashboardProviderGuardVariables(filterElement),
    { fetchPolicy: DASHBOARD_PROVIDER_GUARD_FETCH_POLICY },
  );

  if (!queryRef) {
    return <CircularFallback />;
  }

  return (
    <DashboardProviderCheck dashboardKey={dashboardKey} queryRef={queryRef}>
      {children}
    </DashboardProviderCheck>
  );
}

interface DashboardProviderCheckProps {
  /** The dashboard key from the current route (`:key`). */
  dashboardKey: string;
  /** Rendered when the route already matches the selected provider. */
  children: ReactNode;
  /** Reference to the loaded guard query. */
  queryRef: PreloadedQuery<DashboardProviderGuardQuery>;
}

function DashboardProviderCheck({
  dashboardKey,
  children,
  queryRef,
}: DashboardProviderCheckProps) {
  const provider = useProvider();

  const data = usePreloadedQuery<DashboardProviderGuardQuery>(
    dashboardProviderGuardQuery,
    queryRef,
  );

  const keys = (data.dashboards.edges ?? []).map((edge) => edge.node.key);

  if (keys.includes(dashboardKey)) {
    return children;
  }

  const target = keys.find(isCostDashboardKey);
  if (target) {
    return (
      <Navigate to={generatePath(paths.dashboard, { key: target })} replace />
    );
  }

  // This provider has no cost dashboard (e.g. Tencent Cloud in some
  // environments) — explain the switch rather than silently redirecting away.
  return (
    <EmptyState
      message={`There is no cost dashboard for ${formatProviderFullName(
        provider,
      )}. Select a different provider or report from the sidebar.`}
      title="No cost dashboard"
    />
  );
}
